'use client';

import { Popover as BasePopover } from '@base-ui/react/popover';

import { type ZeusHTMLProps } from '../../internal/types/html-props';
import { type Component, type ComponentProps } from '../../types/component';
import { usePopoverContext } from './internal/PopoverContext';

interface PopoverCloseBaseProps {
  /**
   * Function that renders a close button within the `Popover.Popup`. It receives `props` and `state` as parameters.
   *
   * @param props - The props to apply to the close element.
   * @param state.isOpen - Whether the Popover is open or closed.
   *
   * @return The close button element.
   */
  render: (
    props: ZeusHTMLProps,
    state: { readonly isOpen: boolean }
  ) => React.ReactElement;
}

type PopoverCloseProps = ComponentProps<PopoverCloseBaseProps>;

type PopoverCloseComponent = Component<PopoverCloseBaseProps>;

const PopoverClose: PopoverCloseComponent = (props) => {
  // `PopoverContext.isOpen` is sourced from `Popover` root mirroring Base UI's open state.
  // `BasePopover.Close` does not surface `open` on its own render state, so we rely on context.
  const { isOpen } = usePopoverContext();
  const { render } = props;

  return (
    <BasePopover.Close
      render={(baseProps) => render(baseProps as ZeusHTMLProps, { isOpen })}
    />
  );
};

PopoverClose.displayName = 'Popover.Close';

export { PopoverClose, type PopoverCloseProps, type PopoverCloseBaseProps };
